import { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { API, authHeaders, fmtDate } from "./adminApi";
import { ImageUploadField } from "./ImageUploadField";

const emptySpecial = { name: "", description: "", price: "", regular_price: "", image: "", valid_until: "", active: true };

const SpecialDialog = ({ open, onOpenChange, special, onSaved }) => {
  const [form, setForm] = useState(emptySpecial);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(special ? { ...special } : emptySpecial);
  }, [special, open]);

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const save = async () => {
    if (!form.name.trim() || form.price === "" || Number(form.price) < 0 || !form.valid_until) {
      toast.error("Name, price, and end date are required");
      return;
    }
    setSaving(true);
    try {
      const url = special ? `${API}/api/admin/specials/${special.id}` : `${API}/api/admin/specials`;
      const res = await fetch(url, {
        method: special ? "PUT" : "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ ...form, price: Number(form.price), regular_price: Number(form.regular_price) || 0 }),
      });
      if (!res.ok) {
        const data = await res.json();
        toast.error(data.detail || "Could not save special");
        return;
      }
      toast.success(special ? "Special updated" : "Special added");
      onOpenChange(false);
      onSaved();
    } catch {
      toast.error("Could not save special");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="special-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{special ? "Edit Special" : "Add Special"}</DialogTitle>
          <DialogDescription className="font-body text-xs">
            Specials show on the Experiences page with a countdown until the end date.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="font-body text-sm">Name *</Label>
            <Input data-testid="special-name" value={form.name} onChange={update("name")} className="font-body" />
          </div>
          <div>
            <Label className="font-body text-sm">Description</Label>
            <Textarea data-testid="special-description" value={form.description} onChange={update("description")} rows={2} className="font-body" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="font-body text-sm">Special Price (USD) *</Label>
              <Input data-testid="special-price" type="number" min="0" step="0.01" value={form.price} onChange={update("price")} className="font-body" />
            </div>
            <div>
              <Label className="font-body text-sm">Regular Price (USD)</Label>
              <Input data-testid="special-regular-price" type="number" min="0" step="0.01" value={form.regular_price} onChange={update("regular_price")} className="font-body" />
            </div>
          </div>
          <div>
            <Label className="font-body text-sm">Available Until *</Label>
            <Input data-testid="special-valid-until" type="date" value={form.valid_until} onChange={update("valid_until")} className="font-body" />
          </div>
          <ImageUploadField value={form.image} onChange={(url) => setForm((f) => ({ ...f, image: url }))} testIdPrefix="special-image" />
          <div className="flex items-center gap-2">
            <Switch data-testid="special-active" checked={Boolean(form.active)} onCheckedChange={(v) => setForm({ ...form, active: v })} />
            <span className="font-body text-sm text-foreground">Active (visible & bookable on the site)</span>
          </div>
          <Button data-testid="special-save" onClick={save} disabled={saving} className="w-full bg-primary hover:bg-primary/90 text-white font-body rounded-full">
            {saving ? <Loader2 size={16} className="animate-spin" /> : "Save"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const SpecialsTab = () => {
  const [specials, setSpecials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchSpecials = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/specials`, { headers: authHeaders() });
      if (res.ok) setSpecials(await res.json());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSpecials();
  }, [fetchSpecials]);

  const remove = async (id) => {
    if (!window.confirm("Delete this special?")) return;
    const res = await fetch(`${API}/api/admin/specials/${id}`, { method: "DELETE", headers: authHeaders() });
    if (res.ok) {
      toast.success("Special deleted");
      fetchSpecials();
    } else {
      toast.error("Could not delete special");
    }
  };

  const toggleActive = async (sp) => {
    const res = await fetch(`${API}/api/admin/specials/${sp.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ ...sp, active: !sp.active }),
    });
    if (res.ok) fetchSpecials();
    else toast.error("Could not update special");
  };

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-primary" size={28} /></div>;

  const expired = (sp) => sp.valid_until < new Date().toISOString().slice(0, 10);

  return (
    <div data-testid="admin-specials-tab">
      <div className="flex items-center justify-between mb-5">
        <p className="font-body text-sm text-muted-foreground">{specials.length} specials · limited-time deals with countdown</p>
        <Button data-testid="add-special-button" onClick={() => { setEditing(null); setDialogOpen(true); }} className="bg-accent hover:bg-accent/90 text-white font-body rounded-full">
          <Plus size={16} className="mr-1" /> Add Special
        </Button>
      </div>
      <div className="space-y-2">
        {specials.map((sp) => (
          <div key={sp.id} data-testid={`special-row-${sp.id}`} className={`flex items-center gap-4 rounded-lg border p-4 ${sp.active && !expired(sp) ? "border-border/60 bg-white" : "border-border/40 bg-muted/40 opacity-70"}`}>
            <div className="flex-1 min-w-0">
              <p className="font-heading text-base font-semibold text-foreground">{sp.name}</p>
              <p className="font-body text-xs text-muted-foreground truncate">
                Until {fmtDate(sp.valid_until)}{expired(sp) ? " · EXPIRED" : ""}
              </p>
            </div>
            <div className="shrink-0 text-right">
              <span className="font-heading text-base font-semibold text-primary">${Number(sp.price).toFixed(2)}</span>
              {sp.regular_price > sp.price && (
                <span className="font-body text-xs text-muted-foreground line-through ml-1">${Number(sp.regular_price).toFixed(2)}</span>
              )}
            </div>
            <Switch data-testid={`special-toggle-${sp.id}`} checked={Boolean(sp.active)} onCheckedChange={() => toggleActive(sp)} />
            <button data-testid={`special-edit-${sp.id}`} onClick={() => { setEditing(sp); setDialogOpen(true); }} className="text-muted-foreground hover:text-primary p-1" aria-label="Edit">
              <Pencil size={16} />
            </button>
            <button data-testid={`special-delete-${sp.id}`} onClick={() => remove(sp.id)} className="text-muted-foreground hover:text-destructive p-1" aria-label="Delete">
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>
      <SpecialDialog open={dialogOpen} onOpenChange={setDialogOpen} special={editing} onSaved={fetchSpecials} />
    </div>
  );
};
